import { useEffect, useRef } from 'react';
import { faArrowDown, faMessages } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PostId } from '../model/post.types';
import { ReplyId } from '../model/reply.types';
import { useCommunityPostReplies } from '../api/useCommunityPostReplies';
import { useAddReply } from '../api/useAddReply';
import { useReplyScrollBehavior } from '../lib/useReplyScrollBehavior';
import { useReplyThread } from '../lib/useReplyThread';
import { groupReplies, threadParentId } from '../lib/groupReplies';
import { analytics } from '../lib/analytics';
import { useCommunityContext } from '../lib/communityContext';
import { ReplySkeleton } from './ReplySkeleton';
import { AddReply, AddReplyHandle } from './AddReply';
import { ReplyThreadItem } from './ReplyThreadItem';

const COMMENTS_MAX_HEIGHT_PX = 400;
const MAX_SKELETON_ROWS = 4;

type Props = {
  postId: PostId;
  commentCount: number;
  commentFocusKey?: number;
  onCommentAdded?: () => void;
};

export const PostReplies = ({
  postId,
  commentCount,
  commentFocusKey,
  onCommentAdded,
}: Props) => {
  const { communityId, influencerId, canModerate } = useCommunityContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<AddReplyHandle>(null);

  const { replies, hasMore, isLoadingMore, loadMore } = useCommunityPostReplies(
    communityId,
    postId,
    canModerate,
  );
  const addReply = useAddReply(communityId, postId, canModerate, influencerId);

  const list = replies.type === 'fetched' ? replies.value : [];
  const isInitialLoading =
    replies.type === 'not-fetched' || replies.type === 'fetching';

  const threads = groupReplies(list, postId);
  // The new-comment indicator tracks only the always-visible top-level
  // comments; a new threaded reply lands inside a collapsed thread and is
  // surfaced by that thread's own "N replies" count instead.
  const topLevelComments = threads.map(({ reply }) => reply);
  const mentionNames = [...new Set(list.map((reply) => reply.author.name))];

  const {
    replyTarget,
    expandedThreads,
    expandThread,
    toggleThread,
    startReply,
    clearReplyTarget,
  } = useReplyThread(postId);

  useEffect(() => {
    if (replyTarget) composerRef.current?.focus();
  }, [replyTarget]);

  // Hitting the post-level "Comment" button leaves any reply-to mode.
  useEffect(() => {
    if (!commentFocusKey) return;
    clearReplyTarget();
    composerRef.current?.focus();
  }, [commentFocusKey, clearReplyTarget]);

  const {
    hasNewComment,
    markOwnSubmit,
    markOwnThreadSubmit,
    captureScrollBeforePagination,
    scrollToNewComment,
    handleScroll,
  } = useReplyScrollBehavior({
    scrollRef,
    isInitialLoading,
    lastReplyId: topLevelComments[topLevelComments.length - 1]?.id,
    repliesCount: topLevelComments.length,
  });

  const pendingThreadScrollRef = useRef<{
    parentId: ReplyId;
    childCount: number;
  } | null>(null);

  useEffect(() => {
    const pending = pendingThreadScrollRef.current;
    if (!pending) return;
    const thread = threads.find(({ reply }) => reply.id === pending.parentId);
    if (!thread || thread.children.length <= pending.childCount) return;
    pendingThreadScrollRef.current = null;
    const newest = thread.children[thread.children.length - 1];
    scrollRef.current
      ?.querySelector(`[data-reply-id="${newest.id}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [threads]);

  const onSuccess = (added: Promise<boolean>, track: () => void) =>
    void added.then((ok) => {
      if (ok) track();
    });

  const handleComposerSubmit = (text: string) => {
    if (!replyTarget) {
      markOwnSubmit();
      onSuccess(addReply(text), () => onCommentAdded?.());
      return;
    }
    const parentId = threadParentId(replyTarget, postId);
    const parentCommentAuthorId = replyTarget.author.id;
    const thread = threads.find(({ reply }) => reply.id === parentId);
    markOwnThreadSubmit();
    pendingThreadScrollRef.current = {
      parentId,
      childCount: thread?.children.length ?? 0,
    };
    expandThread(parentId);
    onSuccess(addReply(text, parentId), () =>
      analytics.trackReplyAdded(parentCommentAuthorId),
    );
    clearReplyTarget();
  };

  const handleViewPrevious = () => {
    analytics.trackViewPreviousComments();
    captureScrollBeforePagination();
    loadMore();
  };

  const skeletonRows = Math.min(commentCount, MAX_SKELETON_ROWS);

  return (
    <div className="flex flex-col">
      {isInitialLoading && skeletonRows > 0 && (
        <div
          className="overflow-hidden"
          style={{ maxHeight: `${COMMENTS_MAX_HEIGHT_PX}px` }}
        >
          {Array.from({ length: skeletonRows }, (_, index) => (
            <ReplySkeleton key={index} />
          ))}
        </div>
      )}
      {list.length > 0 && (
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="overflow-y-auto"
          style={{ maxHeight: `${COMMENTS_MAX_HEIGHT_PX}px` }}
        >
          {hasMore && (
            <div className="mb-3 flex justify-center">
              <button
                type="button"
                onClick={handleViewPrevious}
                disabled={isLoadingMore}
                className="flex items-center gap-2 text-sm text-[#8F94A1]"
              >
                <FontAwesomeIcon icon={faMessages} />
                {isLoadingMore
                  ? 'Loading previous comments'
                  : 'View previous comments'}
              </button>
            </div>
          )}
          <div className="flex flex-col divide-y divide-[#EEEEEE]">
            {threads.map(({ reply, children }) => (
              <ReplyThreadItem
                key={reply.id}
                reply={reply}
                childReplies={children}
                postId={postId}
                expanded={expandedThreads.has(reply.id)}
                onToggle={() => toggleThread(reply.id)}
                onReply={startReply}
                mentions={mentionNames}
              />
            ))}
          </div>
        </div>
      )}
      <div className="relative border-t border-[#EEEEEE] pt-5">
        {hasNewComment && (
          <button
            type="button"
            onClick={scrollToNewComment}
            className="absolute -top-10 left-0 right-0 mx-auto flex h-7 w-[175px] items-center justify-center gap-2 rounded-full bg-[#5F1ECC] text-white"
          >
            <FontAwesomeIcon icon={faArrowDown} />
            <span>New Comment</span>
          </button>
        )}
        <AddReply
          ref={composerRef}
          autoFocus
          mentions={mentionNames}
          replyingTo={
            replyTarget
              ? {
                  name: replyTarget.author.name,
                  showMention: !replyTarget.author.isOwn,
                }
              : null
          }
          onCancelReply={clearReplyTarget}
          onSubmit={handleComposerSubmit}
        />
      </div>
    </div>
  );
};
